<template>
    <a
        v-if="!isEmpty(bookInsight)"
        :href="bookInsight.slug"
        :title="bookInsight.title"
        class="book-insight"
        :style="backgroundStyles"
        @click.prevent="handleRouteTrackGTMEvent(bookInsight, $route.name)"
    >
        <div class="content">
            <category-pill>
                <template #category-name>
                    Book Insight
                </template>
            </category-pill>
            <h6>{{ bookInsight.title }}</h6>

            <template v-if="!isEmpty(bookInsight.authors)">
                <p> {{ authorsNames.join(", ") }} </p>
            </template>

            <div class="bi-actions">
                <div v-if="isLocked" class="locked-bi">
                    <svg width="28" height="28" viewBox="0 0 24 24">
                        <path fill="#FFF" fill-rule="nonzero" d="M12.864 14.308v2.946c0 .485-.386.88-.864.88a.872.872 0 0 1-.864-.88v-2.946c0-.485.386-.88.864-.88s.864.395.864.88zM7.383 7.335c0-2.606 2.066-4.721 4.617-4.721 1.225 0 2.4.498 3.266 1.384a4.777 4.777 0 0 1 1.351 3.337V9.42c1.308.422 2.206 1.663 2.21 3.074v5.656c0 1.787-1.416 3.237-3.166 3.237H8.339c-1.75 0-3.167-1.45-3.167-3.237v-5.656c.005-1.411.903-2.652 2.21-3.074V7.335zm7.506 0c0-1.637-1.294-2.962-2.889-2.962-1.595 0-2.889 1.325-2.889 2.961v1.922h5.778V7.334zm1.788 4.113a1.42 1.42 0 0 0-1.016-.431H8.339c-.793 0-1.438.66-1.438 1.476v5.656c0 .816.645 1.476 1.438 1.476h7.322c.793 0 1.438-.66 1.438-1.476v-5.656c0-.392-.152-.768-.422-1.045z" />
                    </svg>
                </div>
                <div
                    v-if="isLoggedIn"
                    class="add-bi-my-library"
                    :class="{ 'deactivated': isWorkingWithLibrary }"
                    @click.stop.prevent="$_toggleAddToLibrary"
                >
                    <svg width="28" height="28" viewBox="0 0 24 24">
                        <path
                            v-if="$_hasBookInsightInLibrary"
                            fill="#B7B7B7"
                            fill-rule="nonzero"
                            d="M18.311 5.535a1.253 1.253 0 0 1 1.774-.004c.477.494.47 1.268-.014 1.752L9.483 17.87a1.255 1.255 0 0 1-1.769-.003l-4.182-4.182a1.252 1.252 0 0 1 0-1.771 1.25 1.25 0 0 1 1.757.02L8.6 15.247l9.711-9.71z"
                        />
                        <path
                            v-else
                            fill="#505050"
                            fill-rule="evenodd"
                            d="M12.9 10.743h6.743a.9.9 0 0 1 0 1.8H12.9v6.744a.9.9 0 1 1-1.8 0v-6.744H4.357a.9.9 0 0 1 0-1.8H11.1V4a.9.9 0 1 1 1.8 0v6.743z"
                        />
                    </svg>
                </div>
            </div>
        </div>
    </a>
</template>

<script>

import _isEmpty from "lodash/isEmpty";
import biBackgroundMixins from "@/mixins/biBackgroundMixins";
import bookInsightLibraryMixins from "@/mixins/bookInsightLibraryMixins.js";
import { mapGetters, mapState } from "vuex";

export default {
    name: "BookInsight",
    components: {
        categoryPill: () => import(/* webpackChunkName: "category-pill"*/ "@c/books-insights/category-pill")
    },
    mixins: [biBackgroundMixins, bookInsightLibraryMixins],
    props: {
        bookInsight: {
            type: Object,
            required: true
        },
        lazyLoading: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        authorsNames() {
            if (!this.bookInsight.authors.length) {
                return [];
            }
            return this.bookInsight.authors.map((author) => author.name);
        },
        ...mapGetters({
            isLoggedIn: "User/isLoggedIn",
            nonFictionSubscriptionDaysLeft: "UserMembership/getNonFictionSubscriptionDaysLeft"
        }),
        ...mapState({
            accessTypeIds: state => state.AccessTypes.idList
        }),
        isLocked() {
            return this.nonFictionSubscriptionDaysLeft <= 0 && this.bookInsight.access_type == this.accessTypeIds.PREMIUM;
        },
        backgroundStyles() {
            if (this.lazyLoading) {
                return "";
            }

            const backgroundStyles = { "background-color" : this.$_backgroundColor, "background-image" : `url(${this.$_backgroundShape})` };
            return backgroundStyles;
        }
    },
    methods: {
        isEmpty: _isEmpty,
        handleRouteTrackGTMEvent(bookInsight, routeName = "") {
            const customEvents = {
                "book-insight": {
                    event: "bi-nav-bookpage-similar",
                    action: "similar"
                }
            };

            if (customEvents.hasOwnProperty(`${routeName}`)) {
                const gtmEvent = {
                    ...customEvents[routeName]
                };

                if (bookInsight.hasOwnProperty("slug")) {
                    gtmEvent.insightSlug = bookInsight.slug
                }

                if (bookInsight.hasOwnProperty("genre")) {
                    gtmEvent.insightGenre = bookInsight.genre.name;
                }

                this.$_gtmTrackEvent(gtmEvent);
            }

            this.$router.push({ name: "book-insight", params: { "slug": bookInsight.slug } });
        }
    }
}
</script>

<style lang="scss" scoped>
.book-insight {
    height: 300px;
    border-radius: 6px;
    overflow: hidden;
    display: block;
    text-decoration: none;
    position: relative;
    cursor: pointer;
    background-repeat: no-repeat;
    background-size: cover;
    background-position: right bottom;

    > div {
        display: flex;
        padding: 25px 20px;
    }

    .content {
        margin-top: 25px;

        .category-pill {
            font-size: 11px;
            font-weight: 500;
            padding: 2px 5px;
            margin-bottom: 15px;
            border-radius: 4px;
        }

        h6 {
            color: #FFFFFF;
            font-size: 24px;
            font-weight: 500;
            line-height: 30px;
            margin-bottom: 0;
            word-break: break-word;
        }

        p {
            color: #FFFFFF;
            font-size: 16px;
            line-height: 22px;
            margin-bottom: 0;
        }

        .bi-actions {
            position: absolute;
            right: 10px;
            bottom: 10px;
            display: flex;

            > div {
                margin-left: 10px;

                &:first-child {
                    margin-left: 0;
                }
            }

            .add-bi-my-library {
                background-color: #ffffff;
                width: 50px;
                height: 50px;
                border-radius: 6px;
                display: flex;
                align-items: center;
                justify-content: center;
                cursor: pointer;
            }

            .locked-bi {
                background-color: rgba(0, 0, 0, .5);
                width: 50px;
                height: 50px;
                border-radius: 6px;
                display: flex;
                align-items: center;
                justify-content: center;
            }
        }
    }

}
</style>

<style lang="scss">
// Fix por IE
.book-insight {
    .content {
        display: inline-block !important;
    }
}
</style>
